import { Button, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { FormSelect } from '../../../components/Select'

interface IProps {
  isOpen: boolean
  /** Anos oferecidos: já cadastrados + ano atual + os 2 seguintes, em ordem decrescente. */
  years: number[]
  /** Ano em uso. Null na primeira entrada, quando o modal é bloqueante. */
  currentYear: number | null
  onSelect: (year: number) => void
  onClose: () => void
}

/**
 * Seleção do ano de referência do cronograma (ABRA-181).
 *
 * Na primeira entrada é bloqueante: sem ano escolhido não há o que listar, então não há
 * botão de fechar nem fechamento pelo overlay. Depois de escolhido, o mesmo modal é
 * reaberto pelo botão "Trocar ano" e aí pode ser cancelado.
 */
export default function YearSelectModal({ isOpen, years, currentYear, onSelect, onClose }: IProps) {
  const isBlocking = currentYear === null
  const [selected, setSelected] = useState<number | null>(currentYear)

  /**
   * `years` chega da API depois da montagem, e o modal já abre na entrada da tela. Sem revalidar
   * quando ela chega, `selected` ficava no ano corrente mesmo que ele não estivesse entre as
   * opções: o Select exibiria o primeiro item da lista enquanto o estado guardava outro, e
   * "Acessar" abriria um ano diferente do que está escrito na tela.
   */
  useEffect(() => {
    if (!isOpen) return

    setSelected((current) => {
      const preferred = currentYear ?? current ?? new Date().getFullYear()

      return years.includes(preferred) ? preferred : (years[0] ?? null)
    })
  }, [isOpen, currentYear, years])

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Cronograma de Turmas"
      onSubmit={() => selected && onSelect(selected)}
      size="compact"
      confirmLabel="Acessar"
      isLoading={years.length === 0}
      isConfirmDisabled={selected === null || years.length === 0}
      isDismissible={!isBlocking}
      // Rodapé próprio: quando o modal bloqueia o acesso, não há Cancelar — sair dele sem
      // escolher um ano deixaria a tela sem dado nenhum.
      footer={
        <>
          {!isBlocking && (
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
          )}
          {/* years.length também barra: enquanto a lista não chega o Select está vazio, e
              entrar num ano que ninguém escolheu não é melhor que esperar um instante. */}
          <Button
            colorScheme="magenta"
            type="submit"
            isLoading={years.length === 0}
            isDisabled={selected === null || years.length === 0}
          >
            Acessar
          </Button>
        </>
      }
    >
      <FormField label="Selecione o ano de referência">
        <FormSelect
          value={selected ?? ''}
          onChange={(value) => setSelected(Number(value))}
          options={years.map((year) => ({ value: year, label: String(year) }))}
          hideEmptyOption
        />
      </FormField>
      <Text variant="caption" mt={3}>
        Um ano passa a existir quando recebe a primeira turma.
      </Text>
    </FormModal>
  )
}
